import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { ModalTypes } from "components/modals/interfaces";
import {
  COORDINATES_SYSTEM_ITEMS,
  LayerAlias,
  UNITS_LIMIT,
  UnitTypes,
  UserPolices
} from "constant";
import { useMapContext } from "context/MapContextProvider";
import features from "features";
import { groupTotalsPointsByClassifications } from "helpers/groupTotalsPointsByClassifications";
import { restrictToParentVerticalBounds } from "helpers/restrictToParentVerticalBounds";
import usePolicy from "hooks/usePolicy";
import { RootStateInterface } from "reducer";
import {
  UnitInterface,
  UnitsManagerStateInterface
} from "scenes/UnitsManager/interfaces";

import Accordion from "components/Accordion/Accordion";
import CommonButton from "components/buttons/CommonButton/CommonButton";
import OptionButton from "components/buttons/OptionButton/OptionButton";
import SecondaryButton from "components/buttons/Secondary/Secondary";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import ExpandBlock from "components/ExpandBlock/ExpandBlock";
import MapAreaListItem from "../MapAreaListItem/MapAreaListItem";
import MapLayer from "../MapLayer/MapLayer";
import EditPolygonPortal from "./EditPolygonPortal";
import EditPolygonsRescalePortal from "./EditPolygonsRescalePortal";

import VerticalDotsIcon from "assets/icons/dots-vertical.svg";
import ExpandedLayersIcon from "assets/icons/expanded-layers.svg";
import LayersIcon from "assets/icons/layers.svg";
import ReportIcon from "assets/icons/report.svg";
import ShareIcon from "assets/icons/share-2.svg";

import styles from "./map-layers.module.scss";

interface MapLayersInterface {
  layers: any;
  aries: any;
  setView: any;
  unitsCollection: any;
  activePolygonsListItem?: any;
  setActivePolygonsListItem?: any;
  openPolygonDrawMode: (data: UnitInterface) => void;
}
const MapLayers = ({
  layers,
  aries,
  setView,
  unitsCollection,
  activePolygonsListItem,
  setActivePolygonsListItem,
  openPolygonDrawMode
}: MapLayersInterface) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPolicy } = usePolicy();
  const { coordinateSystem, updateCoordinatesSystem } = useMapContext();
  const sensors = useSensors(useSensor(PointerSensor));

  const {
    creationPolygonParameters,
    classifications,
    isExportPointsToExcelLoading
  } = useSelector<RootStateInterface, UnitsManagerStateInterface>(
    (state) => state.unitsManager
  );

  const onReport = useCallback(() => {
    navigate("report");
  }, [navigate]);

  const [providersRescalePortalAlias, setProvidersRescalePortalAlias] =
    useState<false | LayerAlias>(false);

  const onShare = useCallback(() => {
    dispatch(
      features.modal.actions.showModal({
        modalType: ModalTypes.SHARE_PROJECT
      })
    );
  }, [dispatch]);

  const classificationsCounters = useMemo(() => {
    return groupTotalsPointsByClassifications(unitsCollection);
  }, [unitsCollection]);

  const isAllPointsByClassificationGroupsVisible = useMemo(() => {
    return Object.keys(classifications)
      .filter((key) => key in classificationsCounters)
      .every((key) => classifications[key]);
  }, [classifications, classificationsCounters]);

  const isDisableExport = useMemo(() => {
    return Object.keys(classifications)
      .filter((key) => key in classificationsCounters)
      .every((key) => !classifications[key]);
  }, [classifications, classificationsCounters]);

  const onChangePointsClassificationActive = useCallback(
    (pointLayer) => () => {
      pointLayer.alias === "all"
        ? dispatch(
            features.unitsManager.actions.setAllPointsByClassificationVisibility(
              {
                isActive: !isAllPointsByClassificationGroupsVisible
              }
            )
          )
        : dispatch(
            features.unitsManager.actions.setPointsByClassificationVisibility({
              alias: pointLayer.alias,
              isActive: !classifications[pointLayer.alias]
            })
          );
    },
    [dispatch, classifications, isAllPointsByClassificationGroupsVisible]
  );

  const handleSeeAllUnits = useCallback(() => {
    dispatch(
      features.unitsManager.actions.fetchUnitsRequest({
        params: {
          projectId: id,
          limit: unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].total,
          skip: UNITS_LIMIT,
          type: UnitTypes.POLYGON,
          alias: LayerAlias.MANUAL
        }
      })
    );
  }, [dispatch, id, unitsCollection]);

  const onEditArea = useCallback(
    (data) => {
      openPolygonDrawMode(data);
    },
    [openPolygonDrawMode]
  );

  const onEditPolygonClose = () => {
    setActivePolygonsListItem(null);
    dispatch(features.unitsManager.actions.clearCreationPolygonParameters());
    dispatch(features.unitsManager.actions.clearCreationPolygonHistory());
  };

  const isSharePolicy = useMemo(
    () => hasPolicy(UserPolices.SHARE_TOKEN_GENERATION),
    [hasPolicy]
  );
  const isReportPolicy = useMemo(
    () => hasPolicy(UserPolices.SHARE_TOKEN_GENERATION),
    [hasPolicy]
  );

  const isAtLeastOneVisiblePolygon = useMemo(
    () =>
      unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units.some(
        (item) => item.isVisible
      ),
    [unitsCollection]
  );

  const isSeeMorePolygonsButtonExists = useMemo(
    () =>
      unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].total > 10 &&
      unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units.length !==
        unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].total,
    [unitsCollection]
  );

  const onAllPolygonsToggleVisibilityClick = useCallback(() => {
    dispatch(
      features.unitsManager.actions.setAllPolygonsVisibility({
        projectId: id,
        isVisible: !isAtLeastOneVisiblePolygon
      })
    );
  }, [dispatch, isAtLeastOneVisiblePolygon, id]);

  const onExportToExcelClick = useCallback(() => {
    const selectedPointsAliases = Object.entries(classifications)
      .filter(([key, value]) => key in classificationsCounters && value)
      .map(([key]) => key);

    dispatch(
      features.unitsManager.actions.fetchPointsExcelRequest({
        params: {
          projectId: id,
          classification_type: selectedPointsAliases
        }
      })
    );
  }, [classifications, classificationsCounters, dispatch, id]);

  const handleRescaleProvidersPortalVisibility = useCallback((val) => {
    setProvidersRescalePortalAlias(val);
  }, []);

  const onUpdateCoordinatesSystem = useCallback(
    (coordinatesSystem) => () => {
      updateCoordinatesSystem(coordinatesSystem.alias);
    },
    [updateCoordinatesSystem]
  );

  const polygonsUnits = useMemo(
    () => unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units,
    [unitsCollection]
  );

  const polygonsUnitsIds = useMemo(
    () => polygonsUnits.map((el) => el.id.toString()),
    [polygonsUnits]
  );

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const newIndex = over.data.current.sortable.index;
        const alias = LayerAlias.MANUAL;
        const unitType = UnitTypes.POLYGON;

        dispatch(
          features.unitsManager.actions.reorderUnitsRequest({
            params: { id, alias, type: unitType, unitId: active.id },
            fields: { index: newIndex },
            onSuccess: () =>
              dispatch(
                features.unitsManager.actions.fetchUnitsRequest({
                  params: {
                    projectId: id,
                    limit: UNITS_LIMIT,
                    skip: 0,
                    type: UnitTypes.POLYGON,
                    alias: LayerAlias.MANUAL
                  }
                })
              )
          })
        );
      }
    },
    [dispatch, id]
  );

  return (
    <Accordion
      accordionScrollWrapperId="mapLayoutScrollWrapperId"
      className={styles["map-layers-accordion"]}
      header={({ isOpen, onToggleAccordion }) => (
        <div
          className={classNames(styles["accordion-head"], {
            [styles["accordion-head--active"]]: isOpen
          })}
          onClick={onToggleAccordion}
        >
          <span className={styles["accordion-head__label"]}>Layers</span>
          <div className={styles["accordion-head__icon"]}>
            <img
              className={styles["icon"]}
              src={LayersIcon}
              alt="layers-button"
            />
            <img
              className={styles["icon--active"]}
              src={ExpandedLayersIcon}
              alt="layers-button"
            />
          </div>
        </div>
      )}
      isOpenDefault
      isScroll
    >
      <div className={styles["layers-content"]}>
        {!!creationPolygonParameters?.id && (
          <EditPolygonPortal
            onClose={onEditPolygonClose}
            activePolygonsListItem={activePolygonsListItem}
          />
        )}
        {providersRescalePortalAlias && (
          <EditPolygonsRescalePortal
            alias={providersRescalePortalAlias}
            onClose={() => handleRescaleProvidersPortalVisibility(false)}
          />
        )}
        <div className={styles["layers-content"]}>
          {layers
            .filter((layer) => layer.alias !== LayerAlias.MANUAL)
            .map((layer, index) => (
              <div
                className={styles["layers-container"]}
                key={`layer-${index}`}
              >
                <MapLayer
                  {...layer}
                  setView={setView}
                  handleRescaleProvidersPortalVisibility={
                    handleRescaleProvidersPortalVisibility
                  }
                />
              </div>
            ))}
        </div>
        <ExpandBlock title="Settings">
          <div className={styles["coordinates__options"]}>
            {COORDINATES_SYSTEM_ITEMS.map((item) => {
              return (
                <OptionButton
                  onClick={onUpdateCoordinatesSystem(item)}
                  key={`layer-param-${item.name}`}
                  isActive={coordinateSystem === item.alias}
                >
                  {item.name}
                </OptionButton>
              );
            })}
          </div>
        </ExpandBlock>

        <ExpandBlock title="Points">
          <div className={styles["layers-container"]}>
            {classificationsCounters?.all?.total > 0 ? (
              <div className={styles["points-by-class-container"]}>
                {Object.entries(classificationsCounters).map(([key, value]) => (
                  <ShowHideButton
                    title={
                      <div className={styles["point-title-container"]}>
                        <span className={styles["point-title-text"]}>
                          {value.label}
                        </span>
                        <span className={styles["point-title-counter"]}>
                          {" "}
                          {value.total}
                        </span>
                      </div>
                    }
                    onClick={onChangePointsClassificationActive({
                      alias: key,
                      ...value
                    })}
                    isActive={
                      key === "all"
                        ? isAllPointsByClassificationGroupsVisible
                        : classifications[key]
                    }
                    key={`btn-${value.label}`}
                  />
                ))}
                <SecondaryButton
                  isLoading={isExportPointsToExcelLoading}
                  onClick={onExportToExcelClick}
                  isDisable={isDisableExport}
                  className={styles["download-excel-button"]}
                >
                  Export to Excel
                </SecondaryButton>
              </div>
            ) : (
              <label className={styles["no-points"]}>No points found.</label>
            )}
          </div>
        </ExpandBlock>

        {/* //TODO: change props to Aliases */}
        <ExpandBlock expandBlockId="areas-expand" title="Areas">
          <div className={styles["layers-container"]}>
            {aries.map((layer, index) => (
              <MapLayer {...layer} key={`layer-${index}`} isPolygon isInit />
            ))}
            {unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON].units
              .length > 0 && (
              <div className={styles["polygons-container"]}>
                <ShowHideButton
                  title={"Polygons"}
                  onClick={onAllPolygonsToggleVisibilityClick}
                  isActive={isAtLeastOneVisiblePolygon}
                />
                <div className={styles["areases-container"]}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                    modifiers={[
                      restrictToVerticalAxis,
                      restrictToParentVerticalBounds
                    ]}
                  >
                    <SortableContext
                      items={polygonsUnitsIds}
                      strategy={verticalListSortingStrategy}
                    >
                      {polygonsUnits.length > 0 &&
                        polygonsUnits.map((unit) => {
                          return (
                            <div key={`${unit.id}`} id={unit.id}>
                              <MapAreaListItem
                                projectId={id}
                                areaData={unit}
                                editUnitAction={onEditArea}
                              />
                            </div>
                          );
                        })}
                    </SortableContext>
                  </DndContext>

                  {isSeeMorePolygonsButtonExists && (
                    <div
                      className={styles["see-all-button"]}
                      onClick={handleSeeAllUnits}
                    >
                      <img src={VerticalDotsIcon} alt="dots" />
                      <span>
                        See all {""}
                        {
                          unitsCollection[LayerAlias.MANUAL][UnitTypes.POLYGON]
                            .total
                        }
                        {""} aries
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ExpandBlock>
        <ExpandBlock title="Other">
          <div className={styles["layers-container"]}>
            <ShowHideButton
              title={"Photos"}
              onClick={() => {}}
              isActive={false}
              isDisabled={true}
            />
          </div>
        </ExpandBlock>
        <ExpandBlock title="Action">
          <div className={styles["layers-container"]}>
            {isReportPolicy && (
              <CommonButton type="button" icon={ReportIcon} onClick={onReport}>
                Create report
              </CommonButton>
            )}
            <div className={styles["spacer"]} />
            {isSharePolicy && (
              <CommonButton type="button" icon={ShareIcon} onClick={onShare}>
                Share project
              </CommonButton>
            )}
          </div>
        </ExpandBlock>
      </div>
    </Accordion>
  );
};
export default MapLayers;
